import {
  useSearchParams,
  useParams,
  useNavigate,
  Link,
} from "react-router-dom";
import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  lazy,
  Suspense,
} from "react";
import { ChevronRight } from "lucide-react";
import toast from "react-hot-toast";

import reviewService from "../../services/reviewService";
import { testcaseService } from "../../services/testcaseService";
import sectionService from "../../services/sectionService";
import sharedStepService from "../../services/sharedStepService";

import { setLastTestCaseForHistory } from "../../utils/testCaseHistoryNav";
import { displayApiDateTime } from "../../utils/formatters";
import SectionTree from "../../components/testcase/SectionTree";
import TableSkeleton from "../../components/skeletons/TableSkeleton";
import { Skeleton } from "../../components/common/Skeleton";

const TestCaseHistoryList = lazy(() => import("./TestCaseHistoryList"));
const TestStepHistoryList = lazy(() => import("./TestStepHistoryList"));

const HISTORY_TAB_KEY = "testCaseHistoryTab";

/** Normalize axios + successResponse body to inner payload */
function unwrapApi(res) {
  if (!res) return null;
  const top = res.data !== undefined ? res.data : res;
  return top?.data !== undefined ? top.data : top;
}

function listFallback() {
  return (
    <div className="rounded-xl border border-[#e5e5ea] bg-white p-4">
      <TableSkeleton rows={5} columns={3} />
    </div>
  );
}

/**
 * Unified History: `/test-cases/history` with `?tab=test_step` for shared steps list.
 * Detail: `/test-cases/history/:testCaseId` (no tabs). Embedded: `testCaseId` prop (slide-in).
 *
 * @param {string} [testCaseId] — When embedded (slide-in panel), pass the selected case id.
 */
const TestCaseHistory = ({ testCaseId: testCaseIdProp }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { testCaseId: testCaseIdParam } = useParams();
  const projectId = searchParams.get("project_id");
  const clientIdFromUrl = searchParams.get("client_id");
  const branchId = searchParams.get("branch_id");
  const suiteId = searchParams.get("suite_id");
  const baselineId = searchParams.get("baseline_id");

  const testCaseId =
    testCaseIdProp || testCaseIdParam || searchParams.get("test_case_id");
  const selectedSectionId = searchParams.get("section_id") || null;

  const embedded = Boolean(testCaseIdProp);
  const showProjectList = Boolean(!embedded && !testCaseId && projectId);

  /** `test_step` opens the Test Step History tab; default = test case list. */
  const tabParam = searchParams.get("tab");
  const tabIsStep = tabParam === "test_step" || tabParam === "step";

  const setHistoryTab = useCallback(
    (nextTab) => {
      const next = new URLSearchParams(searchParams);
      if (nextTab === "test_step") {
        next.set("tab", "test_step");
      } else {
        next.delete("tab");
      }
      setSearchParams(next, { replace: true });
      try {
        localStorage.setItem(
          HISTORY_TAB_KEY,
          nextTab === "test_step" ? "test_step" : "test_case",
        );
      } catch {
        /* ignore */
      }
    },
    [searchParams, setSearchParams],
  );

  /** Legacy `?history_tab=steps` → `?tab=test_step` */
  useEffect(() => {
    if (embedded || testCaseId) return;
    if (searchParams.get("history_tab") !== "steps") return;
    const next = new URLSearchParams(searchParams);
    next.delete("history_tab");
    next.set("tab", "test_step");
    setSearchParams(next, { replace: true });
  }, [embedded, testCaseId, searchParams, setSearchParams]);

  const handleSectionSelect = useCallback(
    (sectionId) => {
      const next = new URLSearchParams(searchParams);
      next.delete("test_case_id");
      if (sectionId) next.set("section_id", sectionId);
      else next.delete("section_id");
      if (testCaseId) {
        navigate(`/test-cases/history?${next.toString()}`);
      } else {
        setSearchParams(next, { replace: true });
      }
    },
    [searchParams, setSearchParams, navigate, testCaseId],
  );

  const [title, setTitle] = useState("");
  const [sectionTrail, setSectionTrail] = useState({ parent: "", leaf: "" });
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedVersions, setExpandedVersions] = useState({});

  const [sectionStructure, setSectionStructure] = useState([]);
  const [sectionLoading, setSectionLoading] = useState(false);

  const [projectHistoryItems, setProjectHistoryItems] = useState([]);
  const [projectListLoading, setProjectListLoading] = useState(false);

  const [stepHistoryItems, setStepHistoryItems] = useState([]);
  const [stepListLoading, setStepListLoading] = useState(false);

  const showSectionSidebar = Boolean(
    !embedded && projectId && !tabIsStep && !testCaseId,
  );

  const loadSectionTree = useCallback(async () => {
    if (!projectId) return;
    setSectionLoading(true);
    try {
      const response = await sectionService.getSectionTree(
        projectId,
        branchId,
        suiteId,
        baselineId,
      );
      const foldersFromAPI =
        response?.sections ||
        response?.data?.sections ||
        response?.data?.folders ||
        response?.folders ||
        (Array.isArray(response) ? response : []);

      const mapFolderToSection = (folder) => ({
        ...folder,
        section_id: folder.section_id || folder.folder_id,
        parent_section_id:
          folder.parent_section_id !== undefined
            ? folder.parent_section_id
            : folder.parent_folder_id,
        children: folder.children
          ? folder.children.map(mapFolderToSection)
          : undefined,
      });

      setSectionStructure(foldersFromAPI.map(mapFolderToSection));
    } catch (e) {
      console.error(e);
      setSectionStructure([]);
    } finally {
      setSectionLoading(false);
    }
  }, [projectId, branchId, suiteId, baselineId]);

  useEffect(() => {
    if (tabIsStep || !projectId) return;
    loadSectionTree();
  }, [loadSectionTree, tabIsStep, projectId]);

  useEffect(() => {
    if (!showProjectList || tabIsStep) return;
    let cancelled = false;
    setProjectListLoading(true);
    reviewService
      .listProjectHistory(
        projectId,
        selectedSectionId || undefined,
        clientIdFromUrl || undefined,
      )
      .then((res) => {
        if (cancelled) return;
        const inner = unwrapApi(res);
        const items = inner?.items ?? inner?.data?.items ?? [];
        setProjectHistoryItems(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err.response?.data?.message || "Failed to load test case list",
          );
          setProjectHistoryItems([]);
        }
      })
      .finally(() => {
        if (!cancelled) setProjectListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    showProjectList,
    tabIsStep,
    projectId,
    selectedSectionId,
    clientIdFromUrl,
  ]);

  useEffect(() => {
    if (!showProjectList || !tabIsStep) return;
    let cancelled = false;
    setStepListLoading(true);
    sharedStepService
      .getSharedSteps(projectId, { sort: "updated_at" })
      .then((res) => {
        if (cancelled) return;
        const inner = unwrapApi(res);
        const raw = inner?.shared_steps ?? inner?.sharedSteps ?? [];
        setStepHistoryItems(Array.isArray(raw) ? raw : []);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err.response?.data?.message || "Failed to load shared steps",
          );
          setStepHistoryItems([]);
        }
      })
      .finally(() => {
        if (!cancelled) setStepListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showProjectList, tabIsStep, projectId]);

  useEffect(() => {
    if (!testCaseId || !projectId) return;
    testcaseService
      .getTestCaseById(projectId, testCaseId)
      .then((res) => {
        const row =
          res?.data != null &&
          typeof res.data === "object" &&
          !Array.isArray(res.data)
            ? res.data
            : res;
        const t = row?.title ?? "";
        setTitle(t);
        const pathStr = row?.section_path || row?.section_name || "";
        if (pathStr && typeof pathStr === "string") {
          const parts = pathStr
            .split(">")
            .map((p) => p.trim())
            .filter(Boolean);
          if (parts.length >= 2) {
            setSectionTrail({
              parent: parts[0],
              leaf: parts[parts.length - 1],
            });
          } else if (parts.length === 1) {
            setSectionTrail({ parent: "", leaf: parts[0] });
          } else if (row?.section_name) {
            setSectionTrail({ parent: "", leaf: row.section_name });
          }
        } else if (row?.section_name) {
          setSectionTrail({ parent: "", leaf: row.section_name });
        }
      })
      .catch(() => {});
  }, [testCaseId, projectId]);

  useEffect(() => {
    if (!testCaseId) {
      setLoading(false);
      setHistoryData([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    reviewService
      .getVersions(testCaseId)
      .then((res) => {
        if (cancelled) return;
        const payload = unwrapApi(res);
        const list = payload?.history ?? payload?.versions ?? [];
        setHistoryData(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err.response?.data?.message || "Failed to load history");
          setHistoryData([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [testCaseId]);

  const rowKey = (h) => h.test_case_id || h.id;

  const toggleExpanded = (key) => {
    setExpandedVersions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleRestore = (versionTestCaseId) => {
    if (!versionTestCaseId) return;
    reviewService
      .createVersion(versionTestCaseId)
      .then(() => {
        toast.success("Version restored");
        return reviewService.getVersions(testCaseId);
      })
      .then((res) => {
        const payload = unwrapApi(res);
        const list = payload?.history ?? payload?.versions ?? [];
        setHistoryData(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Failed to restore");
      });
  };

  const backToTestCasesUrl = useMemo(() => {
    const q = new URLSearchParams(searchParams);
    if (!q.get("project_id") && projectId) q.set("project_id", projectId);
    return `/test-cases?${q.toString()}`;
  }, [searchParams, projectId]);

  const openCaseHistory = (id) => {
    if (!id) return;
    setLastTestCaseForHistory(id);
    const q = new URLSearchParams(searchParams);
    navigate(`/test-cases/history/${id}?${q.toString()}`);
  };

  const openSharedStep = (sharedStepId) => {
    if (!sharedStepId || !projectId) return;
    const q = new URLSearchParams(searchParams);
    navigate(`/shared-steps/edit/${sharedStepId}?${q.toString()}`);
  };

  const noCase = !testCaseId;
  const noProjectContext = !testCaseId && !projectId && !testCaseIdProp;

  const mainInner = (
    <>
      {showProjectList && (
        <>
          <nav
            className="mb-4 flex flex-wrap items-center gap-1 text-sm text-[#565D6D]"
            aria-label="Breadcrumb"
          >
            <Link
              to={backToTestCasesUrl}
              className="font-medium text-[#2563eb] hover:underline"
            >
              Test Cases
            </Link>
            <ChevronRight
              className="h-4 w-4 shrink-0 text-[#9ca3af]"
              aria-hidden
            />
            <span className="font-medium text-[#1a1a1e]">History</span>
          </nav>

          <div className="mb-6 flex gap-0 border-b border-[#e5e5ea]">
            <button
              type="button"
              onClick={() => setHistoryTab("test_case")}
              className={`relative px-4 py-3 text-sm font-semibold transition-colors ${
                !tabIsStep
                  ? "text-[#1a1a1e] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-[#8B4513]"
                  : "text-[#9ca3af] hover:text-[#565D6D]"
              }`}
            >
              Test Case History
            </button>
            <button
              type="button"
              onClick={() => setHistoryTab("test_step")}
              className={`relative px-4 py-3 text-sm font-semibold transition-colors ${
                tabIsStep
                  ? "text-[#1a1a1e] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-[#8B4513]"
                  : "text-[#9ca3af] hover:text-[#565D6D]"
              }`}
            >
              Test Step History
            </button>
          </div>

          <h1 className="mb-2 text-xl font-semibold text-[#1a1a1e]">
            {tabIsStep ? "Test step history" : "Test case history"}
          </h1>
          <p className="mb-4 text-sm text-[#565D6D]">
            {tabIsStep ? (
              <>
                For test case version history, go to{" "}
                <button
                  type="button"
                  onClick={() => setHistoryTab("test_case")}
                  className="font-medium text-[#2563eb] underline"
                >
                  Test case history
                </button>
                .
              </>
            ) : (
              <>
                For shared test steps, go to{" "}
                <button
                  type="button"
                  onClick={() => setHistoryTab("test_step")}
                  className="font-medium text-[#2563eb] underline"
                >
                  Test step history
                </button>
                .
              </>
            )}
          </p>
        </>
      )}
      {!noCase && testCaseId && (
        <h2 className="mb-4 text-lg font-semibold text-[#1a1a1e]">
          {title || "Test case history"}
        </h2>
      )}
      {(sectionTrail.parent || sectionTrail.leaf) && (
        <div className="mb-6 text-sm text-[#565D6D]">
          In section{" "}
          {sectionTrail.parent && (
            <>
              <span className="font-medium text-[#2563eb]">
                {sectionTrail.parent}
              </span>
              {sectionTrail.leaf ? " › " : ""}
            </>
          )}
          {sectionTrail.leaf && (
            <span className="font-medium text-[#2563eb]">
              {sectionTrail.leaf}
            </span>
          )}
        </div>
      )}

      {showProjectList && !tabIsStep && (
        <Suspense fallback={listFallback()}>
          <TestCaseHistoryList
            projectHistoryItems={projectHistoryItems}
            projectListLoading={projectListLoading}
            selectedSectionId={selectedSectionId}
            backToTestCasesUrl={backToTestCasesUrl}
            onOpenCaseHistory={openCaseHistory}
          />
        </Suspense>
      )}

      {showProjectList && tabIsStep && (
        <Suspense fallback={listFallback()}>
          <TestStepHistoryList
            stepHistoryItems={stepHistoryItems}
            stepListLoading={stepListLoading}
            searchParams={searchParams}
            onOpenSharedStep={openSharedStep}
          />
        </Suspense>
      )}

      {noProjectContext && (
        <div className="rounded-2xl border border-[#e5e5ea] bg-white p-10 text-center shadow-sm">
          <h2 className="text-lg font-semibold text-[#1a1a1e]">
            No project selected
          </h2>
          <p className="mt-2 text-sm text-[#565D6D]">
            Open <strong>Test Cases</strong> from the sidebar with a project
            selected, then open <strong>History</strong> again.
          </p>
          <Link
            to="/test-cases"
            className="mt-4 inline-block text-sm font-medium text-[#2563eb] underline"
          >
            Go to Test Cases
          </Link>
        </div>
      )}

      {!noCase && loading && (
        <div className="rounded-xl border border-[#e5e5ea] bg-white p-4">
          <TableSkeleton rows={6} columns={2} />
        </div>
      )}

      {!noCase && !loading && historyData.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[#c5c5ca] bg-white p-10 text-center">
          <p className="text-sm text-[#565D6D]">
            No version history found for this test case yet.
          </p>
        </div>
      )}

      {!noCase && !loading && historyData.length > 0 && (
        <div className="space-y-6">
          {historyData.map((history, index) => (
            <div
              key={
                history.test_case_id ||
                history.id ||
                `v-${history.version}-${index}`
              }
            >
              <div className="rounded-xl border border-[#c7d2fe] bg-white p-6 shadow-sm">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <span
                      className={`rounded-md px-3 py-1 text-xs font-semibold text-white ${
                        history.status === "Created"
                          ? "bg-[#6b7280]"
                          : "bg-[#4b5563]"
                      }`}
                    >
                      {history.status}
                    </span>
                    <span className="text-sm text-[#1a1a1e]">
                      Version: <strong>{history.version}</strong>
                    </span>
                    <span className="text-sm text-[#565D6D]">
                      {displayApiDateTime(history.timestamp || history.date)}
                      {history.updatedBy ? ` · ${history.updatedBy}` : ""}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="rounded-md border border-[#d1d5db] bg-white px-4 py-1.5 text-sm text-[#374151] hover:bg-[#f9fafb]"
                      disabled
                    >
                      Compare To ▼
                    </button>
                    {history.canRestore && history.test_case_id && (
                      <button
                        type="button"
                        onClick={() => handleRestore(history.test_case_id)}
                        className="rounded-md bg-[#22c55e] px-4 py-1.5 text-sm font-medium text-white hover:bg-[#16a34a]"
                      >
                        Restore
                      </button>
                    )}
                  </div>
                </div>

                {history.title && (
                  <div className="mb-4">
                    <div className="mb-1 text-sm font-semibold text-[#374151]">
                      Title
                    </div>
                    <div className="rounded-lg border border-[#e5e7eb] bg-[#f9fafb] px-3 py-2 text-sm text-[#4b5563]">
                      {history.title}
                    </div>
                  </div>
                )}

                {history.references && (
                  <div className="mb-4">
                    <div className="mb-1 text-sm font-semibold text-[#374151]">
                      References
                    </div>
                    <div className="rounded-lg border border-[#e5e7eb] bg-[#f9fafb] px-3 py-2 text-sm text-[#4b5563]">
                      {history.references}
                    </div>
                  </div>
                )}

                {history.steps && history.steps.length > 0 && (
                  <div className="mb-4">
                    <div className="mb-2 text-sm font-semibold text-[#374151]">
                      Steps
                    </div>
                    {history.steps.map((step) => (
                      <div
                        key={step.number}
                        className="mb-4 flex gap-3 border-b border-[#f3f4f6] pb-4 last:mb-0 last:border-0 last:pb-0"
                      >
                        <span className="mt-0.5 font-semibold text-[#1a1a1e]">
                          {step.number}
                        </span>
                        <div className="min-w-0 flex-1 space-y-2">
                          <div>
                            <div className="mb-1 text-xs font-medium text-[#6b7280]">
                              Step description
                            </div>
                            <div className="rounded-md border border-[#e5e7eb] bg-[#f9fafb] px-3 py-2 text-sm text-[#374151]">
                              {step.description}
                            </div>
                          </div>
                          <div>
                            <div className="mb-1 text-xs font-medium text-[#6b7280]">
                              Expected Result
                            </div>
                            <div className="rounded-md border border-[#e5e7eb] bg-[#f9fafb] px-3 py-2 text-sm text-[#374151]">
                              {step.expectedResult}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {history.showPrevious && (
                  <button
                    type="button"
                    onClick={() => toggleExpanded(rowKey(history))}
                    className="text-sm font-medium text-[#2563eb] hover:underline"
                  >
                    {expandedVersions[rowKey(history)]
                      ? "Hide Previous"
                      : "Show Previous"}
                  </button>
                )}

                {history.isCreation && (
                  <p className="mt-3 text-sm leading-relaxed text-[#6b7280]">
                    This test case was created. Changes to this test case are
                    displayed above, separately for each update.
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  if (embedded) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-[#f5f5f7]">
        <div className="flex-1 overflow-y-auto px-4 py-4">{mainInner}</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#f5f5f7]">
      {showSectionSidebar && (
        <aside className="flex w-[280px] shrink-0 flex-col border-r border-[#e5e5ea] bg-white">
          <div className="border-b border-[#e5e5ea] px-3 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#565D6D]">
              Sections
            </p>
            <button
              type="button"
              onClick={() => handleSectionSelect(null)}
              className={`mt-2 w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                !selectedSectionId
                  ? "bg-[#eef2ff] font-medium text-[#3730a3]"
                  : "text-[#1a1a1e] hover:bg-[#f5f5f7]"
              }`}
            >
              All sections
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-1 py-2">
            {sectionLoading ? (
              <div className="space-y-2 px-2 py-1">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-5/6" />
                <Skeleton className="h-8 w-4/5" />
              </div>
            ) : (
              <SectionTree
                sections={sectionStructure}
                currentSectionId={selectedSectionId || undefined}
                onSectionSelect={handleSectionSelect}
                onCreateSection={null}
                onEditSection={null}
                onDeleteSection={null}
                onDropTestCase={null}
                loading={sectionLoading}
                projectStructure={null}
              />
            )}
          </div>
        </aside>
      )}

      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
        <div className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">
          {mainInner}
        </div>
      </div>
    </div>
  );
};

export default TestCaseHistory;
